import { isEmptyString } from '@/utils/string';
import { getPwnedCount } from './hibp';
import { validatePasswordLength } from './password-rules';
import { scorePassword } from './scorer';
import { useDebouncedResult } from '@/hooks/state/use-debounced-result';

/** Matches the debounce the Ember password validator used between keystrokes. */
const DEFAULT_DEBOUNCE_MS = 250;
/** A password seen in breaches more than this many times is rejected (Ember parity). */
const ACCEPTABLE_PWNED_COUNT = 1;
/** Minimum zxcvbn score (0–4) required to pass. */
const MINIMUM_SCORE = 3;

const COMPROMISED_ERROR = 'This password has appeared in known data breaches. Choose a different password.';
const GUESSABLE_ERROR = 'This password is guessable.';

/** Full validation outcome: strength signal for the meter + gate for the form. */
export type PasswordValidationResult = {
  /** zxcvbn score 0–4, or undefined when strength could not be measured. */
  score: number | undefined;
  warning: string | null;
  suggestions: string[];
  /** Blocking error to show and gate submission on, or null when acceptable. */
  error: string | null;
  isValid: boolean;
};

const IDLE_RESULT: PasswordValidationResult = {
  score: undefined,
  warning: null,
  suggestions: [],
  error: null,
  isValid: false,
};

/**
 * Runs the full password gate: length rules → HIBP breach check → zxcvbn score.
 *
 * These checks are advisory UX and are trivially bypassable client-side — the
 * set-password endpoint MUST enforce length, a breach check, and policy
 * server-side. This is not the security boundary.
 *
 * Fails **open** on infrastructure errors so a third-party/network hiccup can't
 * lock users out of setting a password: an unreachable HIBP skips the breach
 * check, and an unavailable scorer skips the strength gate (length still applies).
 * A password is valid only when it passes every check that did run.
 */
export async function evaluatePassword(
  password: string,
  userInputs?: (string | number)[],
): Promise<PasswordValidationResult> {
  const lengthError = validatePasswordLength(password);
  if (lengthError !== null) {
    return { score: 0, warning: null, suggestions: [], error: lengthError, isValid: false };
  }

  let pwnedCount = 0;
  try {
    pwnedCount = await getPwnedCount(password);
  } catch {
    // HIBP unreachable — fail open, rely on the length + strength checks.
  }
  if (pwnedCount > ACCEPTABLE_PWNED_COUNT) {
    return { score: 0, warning: null, suggestions: [], error: COMPROMISED_ERROR, isValid: false };
  }

  let scored: Awaited<ReturnType<typeof scorePassword>> | null = null;
  try {
    scored = await scorePassword(password, userInputs);
  } catch {
    // Strength engine unavailable — fail open on the score gate (length + HIBP stand).
    return { score: undefined, warning: null, suggestions: [], error: null, isValid: true };
  }

  const { score, feedback } = scored;
  if (score < MINIMUM_SCORE) {
    return {
      score,
      warning: feedback.warning,
      suggestions: feedback.suggestions,
      error: isEmptyString(feedback.warning) ? GUESSABLE_ERROR : feedback.warning,
      isValid: false,
    };
  }

  return { score, warning: feedback.warning, suggestions: feedback.suggestions, error: null, isValid: true };
}

type UsePasswordValidationOptions = {
  /** Site/user-specific strings (email, name, …) penalized if they appear in the password. */
  userInputs?: (string | number)[];
  /** @defaultValue 250 */
  debounceMs?: number;
};

type UsePasswordValidation = PasswordValidationResult & {
  /** True while a validation pass for the current password is in flight. */
  isValidating: boolean;
};

/**
 * Debounced password validation for set-password / signup / reset forms. Drives
 * both the strength meter (`score` / `warning` / `suggestions`) and the submit
 * gate (`isValid` / `error`). Ported from the Ember `password-validator`.
 *
 * `isValid` is forced false while a validation is pending, so a form gating on
 * it can never briefly submit a password that hasn't finished (re)validating.
 */
export function usePasswordValidation(
  password: string,
  { userInputs, debounceMs = DEFAULT_DEBOUNCE_MS }: UsePasswordValidationOptions = {},
): UsePasswordValidation {
  const key = JSON.stringify([password, userInputs ?? []]);
  // Local-state debounce (not react-query on purpose): the key is the plaintext
  // password, which shouldn't be retained in the app-global, microfrontend-shared
  // query cache.
  const { result, isPending } = useDebouncedResult<PasswordValidationResult>(
    key,
    () => evaluatePassword(password, userInputs),
    IDLE_RESULT,
    // Skip only a genuinely empty field. Whitespace-only input still runs through
    // evaluatePassword so the length rule surfaces its error rather than silently
    // blocking submit.
    { skip: password.length === 0, debounceMs },
  );

  return { ...result, isValid: result.isValid && !isPending, isValidating: isPending };
}
